import type { Attachment, WorkItemLink } from '@/shell/api/queries'
import { ATTACHMENT_ALLOWED_LABEL, ATTACHMENT_MAX_BYTES, attachmentPreviewKind, attachmentProblem, formatBytes, SCAN_STATUS_LABEL, workItemLinkHost, workItemLinkProblem } from '@gallop/domain'
import { Download01, File06, Link01, Paperclip, XClose } from '@untitledui/icons'
import { useId, useRef, useState } from 'react'
import { Button as AriaButton, Tab, TabList, Tabs } from 'react-aria-components'
import { Badge } from '@/components/base/badges/badges'
import { Input } from '@/components/base/input/input'
import { api, API_URL } from '@/shell/api/client'
import { fmtDateTime } from '@/shell/format'
import { cx } from '@/utils/cx'

/**
 * PRD-017 §3.14 attachments (Sean 2026-09-15 03:14 / 03:16, option A; ADR 0034). One component set for the Mark done
 * dialog ("Attach evidence"), the drawer's Attachments block and the file Close tab's Attachments list, so a daily
 * cash count and a bank statement are dropped, listed and opened the same way everywhere.
 *
 * Rules that live here (and in the API): images / PDF / CSV / xlsx only, 25 MB per file (Q17), refused inline before
 * anything is sent; every row shows its scan state; nothing previews or downloads until the row is Clean.
 */
export const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,application/pdf,text/csv,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx'

/** The bytes route; `download` asks for a Content-Disposition of attachment. Cookies carry the session (credentials: include on img / a). */
export function attachmentContentUrl(id: string, download = false): string {
  return `${API_URL}/attachments/${id}/content${download ? '?download=1' : ''}`
}

/** Upload one file to an item; the API answers 201 with the row at Scanning. One request per file (the 25 MB cap is per request too). */
export async function uploadAttachment(itemId: string, file: File): Promise<Attachment> {
  const fd = new FormData()
  fd.set('file', file, file.name)
  return api<Attachment>(`/work-items/${itemId}/attachments`, { method: 'POST', body: fd })
}

/**
 * PRD-017 v0.18 (Sean, Gallop - Builds 2026-09-21 07:42: "adding a link should be just like adding an attachment to a
 * task. Just defaulted to page"). One Attach affordance, two kinds: a **page** (an http(s) URL, the `work_item_link`
 * row) and a **file** (the `attachment` row). Both land in the item's one Attachments list. Prior art: Asana's and
 * Linear's Attach menu (computer, drive, link under one button).
 */
export type AttachKind = 'page' | 'file'

/** Page | File segmented control for the Attach panel; Page is the default everywhere. `fileDisabledReason` grays File and says why. */
export function AttachKindTabs({ value, onChange, fileDisabledReason = null, testId = 'attach' }: { value: AttachKind, onChange: (k: AttachKind) => void, fileDisabledReason?: string | null, testId?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <Tabs selectedKey={value} onSelectionChange={k => onChange(String(k) as AttachKind)} disabledKeys={fileDisabledReason ? ['file'] : []} className="flex">
        <TabList aria-label="What to attach" className="inline-flex rounded-lg border border-secondary bg-secondary p-0.5 text-xs font-medium" data-testid={`${testId}-tabs`}>
          {(['page', 'file'] as const).map(k => (
            <Tab
              key={k}
              id={k}
              className={({ isSelected, isDisabled, isFocusVisible }) => cx(
                'flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1 outline-none transition-colors',
                isSelected ? 'bg-primary text-primary shadow-xs' : 'text-tertiary hover:text-primary',
                isDisabled ? 'cursor-not-allowed opacity-40 hover:text-tertiary' : '',
                isFocusVisible ? 'ring-2 ring-[var(--shell-focus-ring)]' : '',
              )}
              data-testid={`${testId}-tab-${k}`}
            >
              {k === 'page' ? <Link01 className="size-3.5" aria-hidden /> : <Paperclip className="size-3.5" aria-hidden />}
              {k === 'page' ? 'Page' : 'File'}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      {fileDisabledReason ? <p className="text-xs text-quaternary" data-testid={`${testId}-file-disabled`}>{fileDisabledReason}</p> : null}
    </div>
  )
}

/** The line under a grayed File tab when the instance has no object store (PRD-017 §3.14, edge case 88). */
export const FILES_OFF_REASON = 'Files are off on this environment; pages still work.'

/** URL + optional label for a page, with the domain rule's inline verdict (http(s) only, length caps) under the URL. */
export function PageLinkFields({ url, label, onUrl, onLabel, autoFocus = false, testId = 'attach', ariaPrefix }: { url: string, label: string, onUrl: (v: string) => void, onLabel: (v: string) => void, autoFocus?: boolean, testId?: string, ariaPrefix?: string }) {
  const problem = url.trim().length > 0 ? workItemLinkProblem({ url, label }) : null
  return (
    <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[3fr_2fr]">
      <Input
        {...(ariaPrefix ? { 'aria-label': `${ariaPrefix} URL` } : { label: 'Page' })}
        size="sm"
        type="url"
        autoFocus={autoFocus}
        placeholder="https://www.dropbox.com/…"
        value={url}
        onChange={onUrl}
        isInvalid={problem !== null}
        hint={problem ?? (ariaPrefix ? undefined : 'A Dropbox share link or any https address.')}
        data-testid={`${testId}-url`}
      />
      <Input {...(ariaPrefix ? { 'aria-label': `${ariaPrefix} label` } : { label: 'Label (optional)' })} size="sm" placeholder={ariaPrefix ? 'Label (optional)' : undefined} value={label} onChange={onLabel} data-testid={`${testId}-label`} />
    </div>
  )
}

/** One entry of the item's Attachments list: a page (link row) or a file, ordered by when it was added, oldest first. */
export type AttachmentEntry = { kind: 'page', at: string, link: WorkItemLink } | { kind: 'file', at: string, attachment: Attachment }

export function attachmentEntries(links: ReadonlyArray<WorkItemLink>, attachments: ReadonlyArray<Attachment>): AttachmentEntry[] {
  const out: AttachmentEntry[] = [
    ...links.map(link => ({ kind: 'page' as const, at: link.created_at, link })),
    ...attachments.map(attachment => ({ kind: 'file' as const, at: attachment.uploadedAt, attachment })),
  ]
  return out.sort((x, y) => Date.parse(x.at) - Date.parse(y.at))
}

/** A page row in the Attachments list: kind icon, label (or URL) opening in a new tab, host, Remove when the caller allows it. */
export function PageRow({ link, onRemove, removing }: { link: WorkItemLink, onRemove?: () => void, removing?: boolean }) {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-secondary px-3 py-2 text-sm" data-testid="link-row" data-kind="page">
      <Link01 className="size-4 shrink-0 text-fg-quaternary" aria-hidden data-testid="attachment-kind" data-kind="page" />
      <a href={link.url} target="_blank" rel="noreferrer noopener" className="min-w-0 flex-1 truncate font-medium text-brand-secondary hover:underline" title={link.url} data-testid="link-anchor">
        {link.label ?? link.url}
      </a>
      <span className="shrink-0 text-xs text-quaternary">{workItemLinkHost(link.url)}</span>
      {onRemove
        ? (
            <AriaButton
              aria-label={`Remove page ${link.label ?? link.url}`}
              onPress={onRemove}
              isDisabled={removing}
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-tertiary outline-none hover:bg-secondary hover:text-primary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)] disabled:opacity-40"
              data-testid="link-remove"
            >
              <XClose className="size-4" />
            </AriaButton>
          )
        : null}
    </li>
  )
}

export interface PickedFile {
  /** Stable key for the list while the same file is picked more than once. */
  key: string
  file: File
  /** The inline refusal (type, size); a file with a problem is never sent. */
  problem: string | null
}

/** The key the dialog's upload progress is reported under (the drawer sees File objects, not PickedFile rows). */
export function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

export function pickFiles(list: FileList | File[] | null): PickedFile[] {
  if (!list)
    return []
  return Array.from(list).map(file => ({ key: `${file.name}:${file.size}:${file.lastModified}:${crypto.randomUUID().slice(0, 8)}`, file, problem: attachmentProblem({ name: file.name, mime: file.type, size: file.size }) }))
}

/**
 * Drop zone + picker. `onFiles` gets every picked file with its inline verdict; the parent decides whether to upload
 * now (drawer) or hold until Confirm (Mark done dialog).
 */
export function AttachmentDropZone({ onFiles, disabled, label = 'Attach files', hint, compact = false, testId = 'attachment-dropzone' }: {
  onFiles: (files: PickedFile[]) => void
  disabled?: boolean
  label?: string
  hint?: string
  compact?: boolean
  testId?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const id = useId()
  const take = (list: FileList | null) => {
    const picked = pickFiles(list)
    if (picked.length > 0)
      onFiles(picked)
    if (inputRef.current)
      inputRef.current.value = ''
  }
  return (
    <div
      role="group"
      aria-labelledby={id}
      onDragOver={(e) => {
        if (disabled)
          return
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (disabled)
          return
        e.preventDefault()
        setOver(false)
        take(e.dataTransfer.files)
      }}
      className={cx(
        'flex items-center gap-3 rounded-lg border border-dashed px-3 transition-colors',
        compact ? 'py-2' : 'py-3',
        over ? 'border-brand bg-brand-primary_alt' : 'border-secondary bg-secondary',
        disabled ? 'opacity-50' : '',
      )}
      data-testid={testId}
      data-over={over ? 'true' : 'false'}
    >
      <Paperclip className="size-4 shrink-0 text-fg-quaternary" aria-hidden />
      <div className="min-w-0 flex-1 text-sm">
        <span id={id} className="text-secondary">{label}</span>
        <span className="text-tertiary">
          {' '}
          — drop files here or
          {' '}
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="rounded text-brand-secondary underline outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)] disabled:no-underline"
          data-testid={`${testId}-browse`}
        >
          choose files
        </button>
        <p className="mt-0.5 text-xs text-quaternary">{hint ?? `${ATTACHMENT_ALLOWED_LABEL}; up to ${formatBytes(ATTACHMENT_MAX_BYTES)} each. Scanned before anyone can open them.`}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="sr-only"
        aria-label={label}
        disabled={disabled}
        onChange={e => take(e.currentTarget.files)}
        data-testid={`${testId}-input`}
      />
    </div>
  )
}

/** Files picked but not yet sent (the Mark done dialog holds them until Confirm). */
export function PickedFileList({ files, onRemove, uploading }: { files: PickedFile[], onRemove: (key: string) => void, uploading?: ReadonlyMap<string, 'uploading' | 'done' | string> }) {
  if (files.length === 0)
    return null
  return (
    <ul className="flex flex-col gap-1 text-sm" data-testid="picked-files">
      {files.map((f) => {
        const state = uploading?.get(fileKey(f.file))
        return (
          <li key={f.key} className="flex items-center gap-2" data-testid="picked-file" data-problem={f.problem ? 'true' : 'false'}>
            <File06 className="size-3.5 shrink-0 text-fg-quaternary" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-primary" title={f.file.name}>{f.file.name}</span>
            <span className="shrink-0 text-xs text-quaternary">{formatBytes(f.file.size)}</span>
            {f.problem
              ? <Badge size="sm" color="error">Refused</Badge>
              : state === 'uploading'
                ? <Badge size="sm" color="gray">Uploading</Badge>
                : state === 'done'
                  ? <Badge size="sm" color="success">Sent</Badge>
                  : state
                    ? <Badge size="sm" color="error">Failed</Badge>
                    : null}
            <AriaButton
              aria-label={`Remove ${f.file.name}`}
              onPress={() => onRemove(f.key)}
              isDisabled={state === 'uploading' || state === 'done'}
              className="flex size-6 shrink-0 items-center justify-center rounded-md text-tertiary outline-none hover:bg-secondary hover:text-primary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)] disabled:opacity-40"
              data-testid="picked-file-remove"
            >
              <XClose className="size-3.5" />
            </AriaButton>
            {f.problem ? <p role="alert" className="basis-full text-xs text-error-primary" data-testid="picked-file-problem">{f.problem}</p> : null}
            {state && state !== 'uploading' && state !== 'done' ? <p role="alert" className="basis-full text-xs text-error-primary">{state}</p> : null}
          </li>
        )
      })}
    </ul>
  )
}

const CHIP_COLOR: Record<Attachment['scanStatus'], 'gray' | 'success' | 'error'> = { pending: 'gray', clean: 'success', failed: 'error' }

export function ScanStatusChip({ status }: { status: Attachment['scanStatus'] }) {
  return (
    <Badge size="sm" color={CHIP_COLOR[status]} className={status === 'pending' ? 'animate-pulse' : undefined}>
      <span data-testid="attachment-status" data-status={status}>{SCAN_STATUS_LABEL[status]}</span>
    </Badge>
  )
}

/** Short type word for the row ("PDF", "PNG", "CSV", "XLSX"). */
export function attachmentTypeLabel(mime: string): string {
  if (mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    return 'XLSX'
  if (mime === 'text/csv')
    return 'CSV'
  return mime.split('/')[1]!.toUpperCase()
}

/**
 * One attachment row: name, type, size, uploader, time, status chip, then a preview (image thumbnail inline, PDF in a
 * new tab) and Download once Clean, and Remove when the caller allows it. `extra` renders after the name (the Close
 * tab's item title).
 */
export function AttachmentRow({ a, onRemove, removing, extra }: { a: Attachment, onRemove?: () => void, removing?: boolean, extra?: React.ReactNode }) {
  const clean = a.scanStatus === 'clean'
  const preview = clean ? attachmentPreviewKind(a.mimeType) : null
  return (
    <li className="flex flex-col gap-1 rounded-lg border border-secondary px-3 py-2" data-testid="attachment-row" data-kind="file" data-status={a.scanStatus} data-attachment-id={a.id}>
      <div className="flex items-center gap-2 text-sm">
        {preview === 'image'
          ? (
              <a href={attachmentContentUrl(a.id)} target="_blank" rel="noreferrer noopener" className="shrink-0" aria-label={`Open ${a.name}`} data-testid="attachment-kind" data-kind="file">
                <img src={attachmentContentUrl(a.id)} alt="" crossOrigin="use-credentials" loading="lazy" className="size-10 rounded object-cover ring-1 ring-secondary" data-testid="attachment-thumb" />
              </a>
            )
          : <File06 className="size-4 shrink-0 text-fg-quaternary" aria-hidden data-testid="attachment-kind" data-kind="file" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {clean
              ? <a href={attachmentContentUrl(a.id)} target="_blank" rel="noreferrer noopener" className="min-w-0 truncate font-medium text-brand-secondary hover:underline" title={a.name} data-testid="attachment-name">{a.name}</a>
              : <span className="min-w-0 truncate font-medium text-primary" title={a.name} data-testid="attachment-name">{a.name}</span>}
            {extra}
          </div>
          <p className="truncate text-xs text-tertiary" data-testid="attachment-meta">
            {`${attachmentTypeLabel(a.mimeType)} · ${formatBytes(a.sizeBytes)} · ${a.uploadedBy.name ?? 'someone'} · ${fmtDateTime(a.uploadedAt)}`}
            {a.scanStatus === 'failed' ? ' · blocked by scan; the record stays, the file never opens' : ''}
            {a.scanStatus === 'pending' ? ' · opens once the scan passes' : ''}
          </p>
        </div>
        <ScanStatusChip status={a.scanStatus} />
        {clean
          ? (
              <a href={attachmentContentUrl(a.id, true)} download={a.name} className="flex size-7 shrink-0 items-center justify-center rounded-md text-tertiary outline-none hover:bg-secondary hover:text-primary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]" aria-label={`Download ${a.name}`} data-testid="attachment-download">
                <Download01 className="size-4" aria-hidden />
              </a>
            )
          : null}
        {onRemove
          ? (
              <AriaButton
                aria-label={`Remove attachment ${a.name}`}
                onPress={onRemove}
                isDisabled={removing}
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-tertiary outline-none hover:bg-secondary hover:text-primary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)] disabled:opacity-40"
                data-testid="attachment-remove"
              >
                <XClose className="size-4" />
              </AriaButton>
            )
          : null}
      </div>
    </li>
  )
}
